/** Hook that gives the templates dialog a unified view of
 *  built-in + user templates, grouped by category.
 *
 *  Mirrors `usePalettes` — same pub/sub-on-the-prefs-module pattern
 *  so multiple opened dialogs (or a future "Save as Template"
 *  affordance that lives outside the dialog) all stay in sync. */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BUILTIN_TEMPLATES,
  TEMPLATE_CATEGORY_LABEL,
  type DocumentTemplate,
  type TemplateCategory,
} from './builtin-templates';
import {
  __resetUserTemplatesInMemoryForTests,
  __resetUserTemplatesListenersForTests,
  clearAllUserTemplates,
  createUserTemplate,
  deleteUserTemplate,
  loadUserTemplates,
  renameUserTemplate,
  subscribeToUserTemplates,
  userTemplateToDocumentTemplate,
  type UserTemplate,
} from './user-templates-prefs';

export interface UseTemplatesResult {
  /** All templates (built-in first, then user "custom" by recency). */
  allTemplates: DocumentTemplate[];
  /** Pre-grouped — categories with their templates in display order.
   *  Categories with no templates are omitted. */
  byCategory: ReadonlyArray<{
    category: TemplateCategory;
    label: string;
    templates: DocumentTemplate[];
  }>;
  /** Raw user templates (subset of `allTemplates`). Useful for the
   *  per-template delete / rename affordances. */
  userTemplates: UserTemplate[];
  /** Save the supplied dimensions as a new user template. */
  saveTemplate: (input: { name: string; width: number; height: number; dpi?: number }) => void;
  rename: (id: string, name: string) => void;
  remove: (id: string) => void;
  resetAllUser: () => void;
}

const CATEGORY_ORDER: TemplateCategory[] = ['social', 'print', 'web', 'devices', 'custom'];

export function useTemplates(): UseTemplatesResult {
  const [state, setState] = useState(() => loadUserTemplates());

  useEffect(() => subscribeToUserTemplates(setState), []);

  const allTemplates = useMemo<DocumentTemplate[]>(
    () => [...BUILTIN_TEMPLATES, ...state.templates.map(userTemplateToDocumentTemplate)],
    [state.templates],
  );

  const byCategory = useMemo(() => {
    const buckets = new Map<TemplateCategory, DocumentTemplate[]>();
    for (const t of allTemplates) {
      const arr = buckets.get(t.category) ?? [];
      arr.push(t);
      buckets.set(t.category, arr);
    }
    return CATEGORY_ORDER.filter((cat) => (buckets.get(cat) ?? []).length > 0).map((cat) => ({
      category: cat,
      label: TEMPLATE_CATEGORY_LABEL[cat],
      templates: buckets.get(cat) ?? [],
    }));
  }, [allTemplates]);

  const saveTemplate = useCallback(
    (input: { name: string; width: number; height: number; dpi?: number }) => {
      createUserTemplate(input);
    },
    [],
  );

  const rename = useCallback((id: string, name: string) => {
    if (id.startsWith('builtin:')) return;
    renameUserTemplate(id, name);
  }, []);

  const remove = useCallback((id: string) => {
    if (id.startsWith('builtin:')) return;
    deleteUserTemplate(id);
  }, []);

  const resetAllUser = useCallback(() => {
    clearAllUserTemplates();
  }, []);

  return {
    allTemplates,
    byCategory,
    userTemplates: state.templates,
    saveTemplate,
    rename,
    remove,
    resetAllUser,
  };
}

/** Test-only reset. */
export function __resetTemplatesForTests(): void {
  clearAllUserTemplates();
  __resetUserTemplatesInMemoryForTests();
  __resetUserTemplatesListenersForTests();
}
